import {
  Required,
  IsString,
  DataValidator,
  transformKeys,
  IsNumber,
  ValidateNested,
  IsArray,
} from "@/utils/type-vaildator";
import api from "./api";
import { cosUploadMaterialFile } from "./cos-upload-material-file";

// 原始接口
interface MaterialItemUploadResponse {
  batchId: string;
  failedCount: number;
  failedFiles?: Array<string>;
  folderId: string;
  successCount: number;
  taskIds: Array<string>;
  uploaded: Array<string>;
  successAddItem: Array<SuccessAddItem>;
};

interface SuccessAddItem {
  id: number;
  folder_id: number;
  item_url: string;
  item_type: string;
  user_id: number;
  is_enabled: number;
  tags?: string[];
  vector_library_id?: string;
  vector_library_file_url?: string;
  width: number;
  height: number;
  duration: number;
  fps: number;
  size: number;
  vector_ids?: string;
  use_mark_value: string;
  use_sub_mark_values: string;
  parant_folder_id: number;
  task_id: string;
  task_status: string;
  n8n_task_id?: string;
  create_time: number;
  update_time: number;
  deleted_time: number;
}

// 请求接口
export interface MaterialItemUploadRequest {
  file: File;
  folderId: number;
  tagId: string;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

export class TagData {
  @Required("Tag id cannot be empty")
  @IsNumber("Tag id must be a number")
  id: number;

  @Required("Tag name cannot be empty")
  @IsString("Tag name must be a string")
  tagName: string;

  @IsNumber("Folder id must be a number")
  folderId?: number;
};

export class SuccessAddItemData {
  @Required("ID cannot be empty")
  @IsNumber("ID must be a number")
  id: number;

  @Required("Folder ID cannot be empty")
  @IsNumber("Folder ID must be a number")
  folderId: number;

  @Required("Item type cannot be empty")
  @IsString("Item type must be a string")
  itemType: string;

  @Required("Item URL cannot be empty")
  @IsString("Item URL must be a string")
  itemUrl: string;

  @Required("Vector Library File URL cannot be empty")
  @IsString("Vector Library File URL must be a string")
  vectorLibraryFileUrl: string;

  @Required("Duration cannot be empty")
  @IsNumber("Duration must be a number")
  duration: number;

  @Required("Create time cannot be empty")
  @IsNumber("Create time must be a number")
  createTime: number;

  @Required("Tags cannot be empty")
  @IsArray("Tags must be a TagData array")
  tags: TagData[];

  videoCoverUrl?: string;
  imageCoverUrl?: string;
}

export class MaterialItemUploadData {
  @Required("Batch ID cannot be empty")
  @IsString("Batch ID must be a string")
  batchId: string;

  @Required("Failed count cannot be empty")
  @IsNumber("Failed count must be a number")
  failedCount: number;

  @Required("Success count cannot be empty")
  @IsNumber("Success count must be a number")
  successCount: number;

  // @Required("Task IDs cannot be empty")
  @IsArray("Task IDs must be a string array")
  taskIds: string[];

  @Required("Uploaded files cannot be empty")
  @IsArray("Uploaded files must be a string array")
  uploaded: string[];

  // @Required("Failed files cannot be empty")
  @IsArray("Failed files must be a string array")
  failedFiles?: string[];

  @Required("Folder ID cannot be empty")
  @IsString("Folder ID must be a string")
  folderId: string;

  @Required("Success add items cannot be empty")
  @IsArray("Success add items must be a SuccessAddItemData array")
  successAddItem: SuccessAddItemData[];
}

const transformAndValidateAgent = (
  response: MaterialItemUploadResponse
): MaterialItemUploadData => {
  const transformedData = transformKeys(response);

  try {
    const data = DataValidator.validateOrThrow(transformedData, MaterialItemUploadData);
    if (data.successAddItem.length) {
      data.successAddItem.forEach((item) => {
        if (item.itemType === 'video') {
          // 视频封面
          item.videoCoverUrl = `${item.itemUrl}?x-oss-process=video/snapshot,h_${Math.floor(250 * window.devicePixelRatio)},t_0,f_png,m_fast`;
        }
        if (item.itemType === 'image') {
          // 图片封面
          item.imageCoverUrl = `${item.itemUrl}?x-oss-process=image/resize,s_${Math.floor(250 * window.devicePixelRatio)}`;
        }
      })
    }
    return data;
  } catch (error) {
    console.error("Material Item Upload data validation failed:", error.message);
    throw error;
  }
};

/** 上传素材：先直传 COS 得 URL，再提交 fileUrl 保存 */
export const materialItemUpload = async ({
  file,
  folderId,
  tagId,
  onProgress,
  signal,
}: MaterialItemUploadRequest): Promise<MaterialItemUploadData> => {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const { url: fileUrl } = await cosUploadMaterialFile(file, {
    onProgress: onProgress
      ? (p) => onProgress(Math.round(p * 0.95))
      : undefined,
    signal,
  });

  const response = await api.post<MaterialItemUploadResponse>("/material_item/upload", {
    file_url: fileUrl,
    folder_id: folderId,
    tag_id: tagId,
  }, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const loaded = (progressEvent.loaded * 100) / progressEvent.total;
        onProgress(95 + Math.round(loaded * 0.05));
      }
    },
    timeout: 1000 * 60 * 5, // 5分钟超时
    signal,
  });
  const data = transformAndValidateAgent(response.data);
  onProgress?.(100);
  return data;
};
